import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { RAIN_DAY_MM } from "../../src/weather/usual.ts";
import type { WeatherDay, WeatherSymbol } from "./api.ts";
import { Icon, type IconName } from "./souvenir.ts";
import { degrees, lookedUp, OLD_MS, rainAmount, rainOdds, SYMBOL_LABEL, timeOfYear, type Unit } from "./weather.ts";

// A day's weather, level with its date on Days (features/weather.md). The forecast as a fact: its weather's
// icon, the high and low, and the rain it brings. Usually, quieter and dashed like everything only
// suggested on Days, never with a forecast's sun or cloud. Named when it isn't where we sleep. A tap says
// which it is, for where, from when, and whose it is.

// Souvenir's icon for the day's weather.
export const SYMBOL_ICON: Record<WeatherSymbol, IconName> = {
  clear: "sun",
  partly_cloudy: "partly-cloudy",
  cloudy: "cloud",
  fog: "fog",
  light_rain: "drizzle",
  rain: "rain",
  heavy_rain: "heavy-rain",
  sleet: "sleet",
  snow: "snow",
  thunder: "thunder",
};

// Where the weather comes from, and the forecast's licence, as their terms ask to be credited.
const MET_NORWAY = "https://api.met.no/";
const CC_BY = "https://creativecommons.org/licenses/by/4.0/";
const NASA_POWER = "https://power.larc.nasa.gov/";

export function DayWeather({
  day,
  unit,
  named,
  now,
}: {
  day: WeatherDay;
  unit: Unit;
  // Says where it's for: it isn't where we sleep that night (a day trip, or no stay).
  named: boolean;
  now: Date;
}) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLSpanElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const pop = useRef<HTMLDivElement>(null);
  const id = useId();

  // Closes on a tap anywhere else, and on Escape (before the trip's own Escape hears it).
  useEffect(() => {
    if (!open) return;
    const away = (e: PointerEvent) => !root.current?.contains(e.target as Node) && setOpen(false);
    const onEscape = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      setOpen(false);
      button.current?.focus();
    };
    addEventListener("pointerdown", away);
    addEventListener("keydown", onEscape, { capture: true });
    return () => {
      removeEventListener("pointerdown", away);
      removeEventListener("keydown", onEscape, { capture: true });
    };
  }, [open]);

  // Kept inside the list, 16 px from either side: from where the weather is, how wide this is and where
  // the list's sides are, none of which the shift (or this running past the side before it) moves.
  const [shift, setShift] = useState(0);
  useLayoutEffect(() => {
    if (!open || !root.current || !pop.current) return;
    const frame = root.current.closest(".tm-days-scroll, .tm-days-main")?.getBoundingClientRect();
    const lo = (frame?.left ?? 0) + 16;
    const hi = (frame?.right ?? document.documentElement.clientWidth) - 16;
    const left = root.current.getBoundingClientRect().left;
    const right = left + pop.current.offsetWidth;
    const next = right > hi ? Math.max(lo - left, hi - right) : left < lo ? lo - left : 0;
    if (Math.abs(next - shift) > 0.5) setShift(next);
  });

  // Opened near the bottom of the list, the list scrolls it into sight (up and down only: sideways it's
  // already on the screen).
  useEffect(() => {
    const el = pop.current;
    const list = el?.closest<HTMLElement>(".tm-days-scroll, .tm-days-main");
    if (!open || !el || !list) return;
    const below = el.getBoundingClientRect().bottom + 16 - list.getBoundingClientRect().bottom;
    if (below > 0) list.scrollTop += below;
  }, [open]);

  const high = degrees(day.high, unit);
  const low = degrees(day.low, unit);
  const where = named ? day.where.name : null;
  const rain = day.kind === "forecast" ? rainAmount(day.rain_mm) : null;
  const odds = day.kind === "usual" ? rainOdds(day.rain_days) : null;
  const checked = day.kind === "forecast" ? new Date(day.checked_at) : null;
  const old = checked !== null && now.getTime() - checked.getTime() > OLD_MS;
  const label =
    day.kind === "forecast"
      ? `Forecast${where ? ` for ${where}` : ""}: ${SYMBOL_LABEL[day.symbol]}, ${high} and ${low}${rain ? `, ${rain} of rain` : ""}${old && checked ? `, from ${lookedUp(checked, now)}` : ""}`
      : `Usually${where ? ` in ${where}` : ""}: ${high} and ${low}, ${odds}`;

  return (
    <span className="tm-weather-at" ref={root}>
      <button
        ref={button}
        type="button"
        className={`tm-weather is-${day.kind}`}
        aria-label={label}
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen(!open)}
      >
        {where && <span className="tm-weather-where">{where}</span>}
        {day.kind === "forecast" ? (
          <Icon name={SYMBOL_ICON[day.symbol]} size={16} />
        ) : (
          <span className="tm-weather-usually">Usually</span>
        )}
        <span className="tm-weather-high">{high}</span>
        <span className="tm-weather-low">{low}</span>
        {rain && <span className="tm-weather-rain">{rain}</span>}
        {odds && <span className="tm-weather-rain">{odds}</span>}
      </button>
      {open && (
        <div
          className="tm-weather-pop"
          id={id}
          ref={pop}
          role="dialog"
          aria-label="About this weather"
          style={{ transform: `translateX(${shift}px)` }}
        >
          {day.kind === "forecast" && checked ? (
            <>
              <p className="callout">
                <strong>Forecast</strong> for {day.where.name}, {old ? "from" : "updated"} {lookedUp(checked, now)}
              </p>
              <p className="caption tm-muted">
                {SYMBOL_LABEL[day.symbol]}, a high of {high} and a low of {low}
                {rain ? `, ${rain} of rain` : ", no rain to speak of"}.
              </p>
              <p className="caption tm-muted">
                From{" "}
                <a className="tm-link" href={MET_NORWAY} target="_blank" rel="noreferrer">
                  MET Norway
                </a>
                's forecast (
                <a className="tm-link" href={CC_BY} target="_blank" rel="noreferrer">
                  CC BY 4.0
                </a>
                ), summed up by day.
              </p>
            </>
          ) : day.kind === "usual" ? (
            <>
              <p className="callout">
                <strong>Usually</strong>, for {day.where.name} in {timeOfYear(day.date)}, from {day.years.first} to{" "}
                {day.years.last}
              </p>
              <p className="caption tm-muted">
                Highs around {high}, lows around {low}, {odds}, counting days with {RAIN_DAY_MM} mm of rain or more. Not
                a forecast: that comes about 9 days before.
              </p>
              <p className="caption tm-muted">
                From{" "}
                <a className="tm-link" href={NASA_POWER} target="_blank" rel="noreferrer">
                  NASA POWER
                </a>
                's daily records.
              </p>
            </>
          ) : null}
        </div>
      )}
    </span>
  );
}

// Under Days: where the weather comes from, as their terms ask, and how old a forecast is once it's more
// than a day old (the phone kept it, or the forecast couldn't be reached).
export function WeatherCredit({ oldest, now }: { oldest: Date | null; now: Date }) {
  return (
    <p className="caption tm-muted tm-weather-credit">
      {oldest && now.getTime() - oldest.getTime() > OLD_MS && <>Forecast from {lookedUp(oldest, now)}. </>}
      Weather:{" "}
      <a className="tm-link" href={MET_NORWAY} target="_blank" rel="noreferrer">
        MET Norway
      </a>{" "}
      (forecast,{" "}
      <a className="tm-link" href={CC_BY} target="_blank" rel="noreferrer">
        CC BY 4.0
      </a>
      ),{" "}
      <a className="tm-link" href={NASA_POWER} target="_blank" rel="noreferrer">
        NASA POWER
      </a>{" "}
      (usually)
    </p>
  );
}
